import { Text, VStack } from '@chakra-ui/react';
import { ReactNode } from 'react';

import Button from '../../../../../../components/ui/buttons/Button';
import { trackParkedRelistClicked } from '../../../../../../services/analytics/marketplaceEvents';
import MarketplaceCard from '../../MarketplaceCard';
import { LeadGuidance, ParkedPath, toEstimatedMoney } from '../../guidance';
import useGateDecision from '../gate/useGateDecision';
import ExploreBuyoutButton from '../waiting/ExploreBuyoutButton';

type ParkedWayBackCardsProps = {
  path: ParkedPath;
  guidance: LeadGuidance;
  externalId: string;
  dealId: string;
  leadId: string;
  model?: string | null;
  canRelist: boolean;
};

const WayBackCard = ({
  title,
  body,
  children,
}: {
  title: string;
  body: string;
  children: ReactNode;
}) => (
  <MarketplaceCard px="20px" py="18px">
    <VStack align="stretch" spacing="8px">
      <Text fontSize="15px" fontWeight="700" color="leaseEndBlue">
        {title}
      </Text>
      <Text fontSize="12.5px" color="charcoal" lineHeight="1.5">
        {body}
      </Text>
      {children}
    </VStack>
  </MarketplaceCard>
);

const ParkedWayBackCards = ({
  path,
  guidance,
  externalId,
  dealId,
  leadId,
  model,
  canRelist,
}: ParkedWayBackCardsProps) => {
  const { record, pendingDecision, errorMessage } = useGateDecision(externalId);

  const sellOutOfPocket =
    guidance.sellNowNet != null ? toEstimatedMoney(guidance.sellNowNet) : null;
  const sellSentence = (opener: string) =>
    sellOutOfPocket != null
      ? `Your listing is saved and ready. ${opener} you'd pay about ${sellOutOfPocket} out of pocket at signing.`
      : 'Your listing is saved and ready.';

  const handleRelist = () => {
    trackParkedRelistClicked(dealId, leadId, path);
    record('list_anyway');
  };

  const relistAction = (
    <>
      <Button
        onClick={handleRelist}
        isLoading={pendingDecision != null}
        variant="quietOutline"
        size="sm"
        h="36px"
        alignSelf="flex-start"
        mt="4px"
      >
        List my car instead
      </Button>
      {errorMessage != null && (
        <Text fontSize="12px" color="leaseEndRed" lineHeight="1.5">
          {errorMessage}
        </Text>
      )}
    </>
  );

  if (path === 'buyout') {
    if (!canRelist) return null;
    return (
      <WayBackCard title="Changed your mind?" body={sellSentence('If you sell instead,')}>
        {relistAction}
      </WayBackCard>
    );
  }

  return (
    <>
      <WayBackCard title="Rather sell it?" body={sellSentence('If you sell,')}>
        {relistAction}
      </WayBackCard>
      <WayBackCard
        title="Love the car?"
        body="You can own it outright. Our buyout team walks you through financing and what it takes."
      >
        <ExploreBuyoutButton
          source="parked_finish_lease"
          model={model}
          dealId={dealId}
          leadId={leadId}
          variant="quietOutline"
          alignSelf="flex-start"
          mt="4px"
        >
          Explore the buyout
        </ExploreBuyoutButton>
      </WayBackCard>
    </>
  );
};

export default ParkedWayBackCards;
